/**
 * NavigationManager handles back button interception for sidebars and modals
 * on mobile devices. When a sidebar or modal is open and the user presses back,
 * it will close the overlay first before navigating.
 */

import { isBrowser } from './isServer';

type OverlayType = 'sidebar' | 'modal';

interface OverlayState {
  type: OverlayType;
  id: string;
  closeCallback: () => void;
}

class NavigationManager {
  private static instance: NavigationManager;
  private activeOverlays: OverlayState[] = [];
  private isIntercepting = false;
  private originalPopstateHandlers: ((event: PopStateEvent) => void)[] = [];

  private constructor() {
    if (isBrowser) {
      this.setupInterceptor();
    }
  }

  public static getInstance(): NavigationManager {
    if (!NavigationManager.instance) {
      NavigationManager.instance = new NavigationManager();
    }
    return NavigationManager.instance;
  }

  private setupInterceptor(): void {
    // Store original popstate handlers
    const originalHandler = window.onpopstate;
    if (originalHandler) {
      this.originalPopstateHandlers.push(originalHandler);
    }

    // Override popstate event
    window.addEventListener('popstate', (event: PopStateEvent) => {
      if (this.handleBackNavigation(event)) {
        // Prevent default navigation
        event.stopImmediatePropagation();
      }
    }, true); // Use capture phase to intercept before other handlers
  }

  private handleBackNavigation(event: PopStateEvent): boolean {
    // If we have active overlays, close the most recent one
    if (this.activeOverlays.length > 0 && !this.isIntercepting) {
      this.isIntercepting = true;
      
      // Get the most recent overlay
      const overlay = this.activeOverlays[this.activeOverlays.length - 1];
      
      // Push state back to maintain history position
      window.history.pushState(event.state, '', window.location.href);
      
      // Close the overlay
      overlay.closeCallback();
      
      // Remove from stack
      this.activeOverlays.pop();
      
      // Reset flag
      setTimeout(() => {
        this.isIntercepting = false;
      }, 100);
      
      return true; // Prevent default navigation
    }
    
    return false; // Allow normal navigation
  }

  /**
   * Register an overlay (sidebar or modal) that should intercept back navigation
   */
  public registerOverlay(type: OverlayType, id: string, closeCallback: () => void): void {
    if (!isBrowser) return;

    // Check if already registered
    const existingIndex = this.activeOverlays.findIndex(o => o.id === id);
    if (existingIndex !== -1) {
      // Update the callback
      this.activeOverlays[existingIndex].closeCallback = closeCallback;
      return;
    }

    this.activeOverlays.push({ type, id, closeCallback });

    // Push a history state when opening overlay
    // This ensures the back button has something to "go back" to
    window.history.pushState(
      { overlayOpen: true, overlayId: id },
      '',
      window.location.href
    );
  }

  /**
   * Unregister an overlay when it's closed
   */
  public unregisterOverlay(id: string): void {
    this.activeOverlays = this.activeOverlays.filter(o => o.id !== id);
  }

  /**
   * Check if any overlays are currently active
   */
  public hasActiveOverlays(): boolean {
    return this.activeOverlays.length > 0;
  }

  /**
   * Get the count of active overlays
   */
  public getActiveOverlayCount(): number {
    return this.activeOverlays.length;
  }

  /**
   * Clear all overlays (useful for cleanup)
   */
  public clearAllOverlays(): void {
    this.activeOverlays = [];
  }
}

export default NavigationManager;